import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type ErrorMap = Record<string, string>;

type ErrorContextValue = {
  errors: ErrorMap;
  setError: (key: string, message: string | null) => void;
  clearError: (key: string) => void;
};

const ErrorContext = createContext<ErrorContextValue | undefined>(undefined);

export function ErrorProvider({ children }: { children: React.ReactNode }) {
  const [errors, setErrors] = useState<ErrorMap>({});

  const setError = useCallback((key: string, message: string | null) => {
    setErrors(prev => {
      const next = { ...prev };
      if (!message) {
        delete next[key];
      } else {
        next[key] = message;
      }
      return next;
    });
  }, []);

  const clearError = useCallback((key: string) => {
    setError(key, null);
  }, [setError]);

  const value = useMemo<ErrorContextValue>(() => ({ errors, setError, clearError }), [clearError, errors, setError]);

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
}

export function useErrorContext() {
  const ctx = useContext(ErrorContext);
  if (!ctx) {
    throw new Error('useErrorContext must be used within ErrorProvider');
  }
  return ctx;
}
